// Makkerspil: indgangen (lav et rum eller skriv en kode) og rummet med kode, link, QR og banevalg.
// Ren visning: src/net/online.ts ejer forbindelsen og kalder update(), når rummet ændrer sig.

import { LEVELS, getLevel } from '../../content/levels';
import type { NetStatus } from '../../net/room-client';
import type { ErrorCode, RoomView, Seat } from '../../net/protocol';
import { CODE_ALPHABET, normalizeCode } from '../../net/protocol';
import { DIFFICULTIES, DIFFICULTY_ORDER } from '../../sim';
import type { DifficultyId } from '../../sim';
import { h, setAttr, setText } from '../dom';
import { ICON } from '../icons';
import { qrSvg } from '../qr';
import { diffRules } from './home';


/** Makkerspillets roller: regler, ikke råd. Hver handling er én rolles (src/sim/roles.ts). */
export const ROLE: Record<Seat, { name: string; job: string }> = {
  p1: { name: 'Planlægger', job: 'bygger og opgraderer ledninger og stationer.' },
  p2: { name: 'Driftsvagt', job: 'forstærker master, sender montører, jager gæster og har nødknapperne.' },
};

export type RoomError = ErrorCode | 'replaced' | 'net' | 'nocode';

/** Korte, venlige fejltekster. "version" er ordret som i DESIGN.md. */
export const ROOM_ERROR: Record<RoomError, string> = {
  full: 'Rummet er fuldt. Der er kun plads til to.',
  version: 'Ny version. Genindlæs.',
  gone: 'Vi kan ikke finde rummet. Tjek koden, eller lav et nyt.',
  bad: 'Noget gik skævt. Prøv igen.',
  rate: 'Hov, mange forsøg på kort tid. Vent et øjeblik.',
  not_host: 'Det er værten, der vælger bane og trykker start.',
  replaced: 'Du spiller videre i en anden fane.',
  net: 'Ingen forbindelse til rummet. Tjek nettet.',
  nocode: 'En kode er fire tegn, fx K7QX.',
};

/** Linket havde en kode, der ikke kan findes (fx O og 0 forvekslet). */
export const BAD_LINK = 'Den kode findes ikke. Koder bruger ikke O, 0, I, 1 eller L. Tjek linket, eller skriv koden her.';

export const SEAT_NAME: Record<Seat, string> = { p1: 'Grøn', p2: 'Petrol' };

/** Spillerens mærke: p1 er en grøn cirkel, p2 en petrol firkant (form og farve, ikke kun farve). */
export function seatMark(seat: Seat, extra = ''): HTMLElement {
  return h('span', { class: `seat-mark ${seat}${extra ? ` ${extra}` : ''}`, 'aria-hidden': 'true' });
}

export function shareLink(code: string): string {
  return `${location.origin}/r/${code}`;
}

// ---------------------------------------------------------------------------
// Indgang

export interface EntryHandlers {
  create(): void;
  join(code: string): void;
  back(): void;
}

export interface EntryOpts {
  busy?: boolean;
  error?: RoomError | null;
  /** Egen fejltekst i stedet for ROOM_ERROR (fx et ugyldigt link). */
  message?: string;
  code?: string;
  /** En knap lige under fejlen, fx "Spil her" efter en anden fane eller "Genindlæs". */
  fix?: { label: string; run(): void };
}

export function roomEntryScreen(handlers: EntryHandlers, opts: EntryOpts = {}): HTMLElement {
  const input = h('input', {
    class: 'code-input',
    type: 'text',
    inputmode: 'text',
    autocomplete: 'off',
    autocapitalize: 'characters',
    spellcheck: 'false',
    maxlength: 4,
    placeholder: 'K7QX',
    'aria-label': 'Rumkode med fire tegn',
    value: opts.code ?? '',
  }) as HTMLInputElement;
  const err = h('p', { class: 'room-error', role: 'alert' }, opts.message ?? (opts.error ? ROOM_ERROR[opts.error] : ''));
  const fix = opts.fix
    ? h('button', { type: 'button', class: 'btn primary fix', autofocus: true, onclick: () => opts.fix?.run() }, h('span', { html: ICON.bolt }), opts.fix.label)
    : null;
  const go = (): void => {
    const c = normalizeCode(input.value);
    if (!c) {
      setText(err, ROOM_ERROR.nocode);
      input.focus();
      return;
    }
    handlers.join(c);
  };
  input.addEventListener('input', () => {
    // Kun tegn fra kodens alfabet (uden O, 0, I, 1 og L, som er nemme at forveksle).
    const clean = [...input.value.toUpperCase()].filter((ch) => CODE_ALPHABET.includes(ch)).join('').slice(0, 4);
    if (clean !== input.value) input.value = clean;
    setText(err, '');
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      go();
    }
  });
  return h(
    'div',
    { class: 'card room-card entry' },
    h('div', { class: 'title-block' }, h('p', { class: 'eyebrow' }, 'Spil med en ven'), h('h1', { class: 'title small' }, 'TO OM NETTET')),
    h('p', { class: 'lead' }, 'Hver sin telefon, samme net. I deler kassen og redder byerne sammen.'),
    fix ? h('div', { class: 'room-fix' }, err, fix) : null,
    h(
      'button',
      { type: 'button', class: `btn ${fix ? 'secondary' : 'primary'} big`, autofocus: !fix, disabled: opts.busy, onclick: () => handlers.create() },
      h('span', { html: ICON.bolt }),
      opts.busy ? 'Laver rummet…' : 'Lav et rum',
    ),
    h('h2', { class: 'section' }, 'Har du en kode?'),
    h('div', { class: 'code-row' }, input, h('button', { type: 'button', class: 'btn secondary', disabled: opts.busy, onclick: go }, 'Gå ind')),
    fix ? null : err,
    h('div', { class: 'actions' }, h('button', { type: 'button', class: 'btn ghost', onclick: () => handlers.back() }, 'Tilbage')),
  );
}

// ---------------------------------------------------------------------------
// Rummet

/** Linket som tekst: værtsnavnet må klippes med …, men /r/KODE står altid helt og fremhævet. */
export function shareLinkEl(link: string): HTMLElement {
  const bare = link.replace(/^https?:\/\//, '');
  const cut = bare.indexOf('/');
  const host = cut >= 0 ? bare.slice(0, cut) : bare;
  const path = cut >= 0 ? bare.slice(cut) : '';
  return h(
    'a',
    { class: 'share-link', href: link, title: bare, onclick: (e: Event) => e.preventDefault() },
    h('span', { class: 'share-host' }, host),
    h('b', { class: 'share-code' }, path),
  );
}

export interface LobbyHandlers {
  setup(level?: string, difficulty?: DifficultyId): void;
  start(): void;
  /** Spor: coop: byt roller (Planlægger og Driftsvagt). Begge må, kun i lobbyen. */
  swap?(): void;
  leave(): void;
  /** Kun i spillets menu: tilbage til banen. */
  resume?(): void;
}

const BLURB: Record<DifficultyId, string> = {
  laerling: 'God tid',
  vagthavende: 'Som en rigtig vagt',
  systemansvarlig: 'N-1: to veje ind',
  orkan: 'Alt på én gang',
};

export class LobbyScreen {
  readonly el: HTMLElement;
  private seats: HTMLElement;
  private wait: HTMLElement;
  private pick: HTMLElement;
  private guestNote: HTMLElement;
  private brief: HTMLElement;
  private startBtn: HTMLButtonElement;
  private swapBtn: HTMLButtonElement;
  private levelBtns = new Map<string, HTMLButtonElement>();
  private diffBtns = new Map<DifficultyId, HTMLButtonElement>();
  private status: HTMLElement;
  private shareEl: HTMLElement;
  private copied = 0;

  constructor(
    readonly code: string,
    handlers: LobbyHandlers,
    inGame = false,
  ) {
    const link = shareLink(code);
    const copyBtn = h('button', { type: 'button', class: 'btn ghost copy' }, typeof navigator.share === 'function' ? 'Del link' : 'Kopiér link') as HTMLButtonElement;
    copyBtn.addEventListener('click', () => {
      void this.share(link, copyBtn);
    });
    this.seats = h('div', { class: 'seats', 'aria-live': 'polite' });
    this.wait = h(
      'div',
      { class: 'waiting', role: 'status' },
      h('span', { class: 'wait-plug', 'aria-hidden': 'true' }, h('i', {}), h('i', {}), h('i', {})),
      h('span', {}, 'Venter på makker'),
    );
    this.status = h('p', { class: 'net-line', role: 'status' }, '');
    const levels = h(
      'div',
      { class: 'levels compact', role: 'radiogroup', 'aria-label': 'Bane' },
      ...LEVELS.map((lv) => {
        const b = h(
          'button',
          { type: 'button', role: 'radio', class: 'level-card', 'aria-checked': 'false', onclick: () => handlers.setup(lv.id) },
          h('span', { class: 'level-num' }, String(lv.number)),
          h('span', { class: 'level-text' }, h('b', {}, lv.title), h('span', {}, String(lv.year))),
        ) as HTMLButtonElement;
        this.levelBtns.set(lv.id, b);
        return b;
      }),
    );
    const diffs = h(
      'div',
      { class: 'diffs', role: 'radiogroup', 'aria-label': 'Sværhed' },
      ...DIFFICULTY_ORDER.map((id) => {
        const b = h(
          'button',
          { type: 'button', role: 'radio', class: 'diff', 'aria-checked': 'false', onclick: () => handlers.setup(undefined, id) },
          h('b', {}, DIFFICULTIES[id].name),
          h('span', {}, BLURB[id]),
          diffRules(id),
        ) as HTMLButtonElement;
        this.diffBtns.set(id, b);
        return b;
      }),
    );
    // Skjult, til rummet har sagt, hvem der er vært (IGN runde 2: gæsten så værtens knapper et øjeblik).
    this.pick = h('div', { class: 'room-pick guest' }, h('h2', { class: 'section' }, 'Bane'), levels, h('h2', { class: 'section' }, 'Sværhed'), diffs);
    this.guestNote = h('p', { class: 'guest-note' }, '');
    // IGN runde 2: makkerspillet manglede en kort forklaring og hver sin rolle. Spor: coop: rollerne
    // er en ærlig grænse. Et tryk på byen viser kun ens egne knapper; makkerens kan man spørge om.
    this.brief = h('div', { class: 'coop-brief', hidden: true });
    this.startBtn = h(
      'button',
      { type: 'button', class: 'btn primary big room-start', hidden: true, onclick: () => handlers.start() },
      h('span', { html: ICON.play }),
      h('span', { class: 'start-label' }, 'Start sammen'),
    ) as HTMLButtonElement;
    this.swapBtn = h(
      'button',
      { type: 'button', class: 'btn ghost swap-roles', onclick: () => handlers.swap?.() },
      'Byt roller',
    ) as HTMLButtonElement;
    this.shareEl = h(
      'div',
      { class: 'share' },
      h('div', { class: 'qr', html: qrSvg(link, `QR-kode til rum ${code}`) }),
      h(
        'div',
        { class: 'share-text' },
        h('p', {}, inGame ? 'Mistet makkeren? Samme kode virker igen.' : 'Send linket, eller lad din makker skanne koden.'),
        shareLinkEl(link),
        copyBtn,
      ),
    );
    const tiles = h('div', { class: 'code-tiles', 'aria-label': `Rumkode ${code.split('').join(' ')}`, role: 'img' }, ...code.split('').map((ch, i) => h('span', { style: `--i:${i}` }, ch)));
    this.el = h(
      'div',
      { class: `card room-card lobby${inGame ? ' in-game' : ''}` },
      h('div', { class: 'title-block' }, h('p', { class: 'eyebrow' }, inGame ? 'I spiller sammen' : 'Jeres rum'), tiles),
      this.shareEl,
      this.seats,
      inGame || !handlers.swap ? null : this.swapBtn,
      this.wait,
      this.status,
      inGame ? null : this.pick,
      inGame ? null : this.brief,
      inGame ? null : this.guestNote,
      inGame && handlers.resume
        ? h('button', { type: 'button', class: 'btn primary big', autofocus: true, onclick: () => handlers.resume?.() }, h('span', { html: ICON.play }), 'Tilbage til spillet')
        : this.startBtn,
      h('div', { class: 'actions' }, h('button', { type: 'button', class: 'btn ghost', onclick: () => handlers.leave() }, 'Forlad rummet')),
    );
  }

  private async share(link: string, btn: HTMLButtonElement): Promise<void> {
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: 'Fuld spænding', text: `Spil med mig! Kode ${this.code}`, url: link });
        return;
      }
      await navigator.clipboard.writeText(link);
      setText(btn, 'Kopieret!');
      window.clearTimeout(this.copied);
      this.copied = window.setTimeout(() => setText(btn, 'Kopiér link'), 1800);
    } catch {
      // Delingen blev afbrudt eller udklipsholderen er blokeret: linket står der stadig.
    }
  }

  update(room: RoomView, you: Seat, net: NetStatus): void {
    const other: Seat = you === 'p1' ? 'p2' : 'p1';
    const partner = room.seats[other];
    const host = room.host === you;
    this.seats.replaceChildren(
      ...(['p1', 'p2'] as const).map((s) => {
        const v = room.seats[s];
        const who = `${s === you ? 'Dig' : 'Makker'} · ${ROLE[s].name}`;
        const state = !v ? 'Ledig plads' : v.on ? (room.host === s ? 'Vælger bane' : 'Klar') : 'Lige væk';
        return h(
          'div',
          { class: `seat ${s}${v ? '' : ' empty'}${v && !v.on ? ' away' : ''}` },
          seatMark(s),
          h('span', { class: 'seat-text' }, h('b', {}, who), h('span', {}, state)),
        );
      }),
    );
    this.wait.hidden = !!partner?.on;
    // IGN-løkke runde 1: når makkeren er her, er QR og link spildplads, og "Start sammen" røg under
    // folden på en telefon. Koden står stadig øverst, hvis makkeren falder ud.
    this.shareEl.hidden = !!partner?.on;
    setText(this.status, net === 'open' ? '' : net === 'reconnecting' ? 'Forbinder igen…' : net === 'connecting' ? 'Forbinder…' : '');
    for (const [id, b] of this.levelBtns) {
      setAttr(b, 'aria-checked', String(id === room.level));
      b.disabled = !host;
    }
    for (const [id, b] of this.diffBtns) {
      setAttr(b, 'aria-checked', String(id === room.difficulty));
      b.disabled = !host;
    }
    this.pick.classList.toggle('guest', !host);
    const lv = getLevel(room.level);
    setText(this.guestNote, host ? '' : `Din makker vælger. Lige nu: ${lv.title} på ${DIFFICULTIES[room.difficulty].name}.`);
    this.guestNote.hidden = host;
    this.brief.hidden = !partner?.on;
    if (partner?.on) {
      const mine = ROLE[you];
      this.brief.replaceChildren(
        h('h2', { class: 'section' }, 'Sådan spiller I'),
        h('p', {}, h('b', {}, `Du er ${mine.name.toLowerCase()}: `), mine.job),
        h('p', {}, h('b', {}, `Makker er ${ROLE[other].name.toLowerCase()}: `), ROLE[other].job),
        h('p', {}, 'I deler kassen. Mangler du makkers knap, så tryk alligevel: det bliver et spørgsmål. Langt tryk sender et ping.'),
      );
    }
    const ready = !!partner?.on && net === 'open';
    this.startBtn.hidden = !host;
    this.startBtn.disabled = !ready;
    setText(this.startBtn.querySelector('.start-label') as HTMLElement, ready ? 'Start sammen' : 'Venter på makker…');
  }
}
